import { useEffect, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc.js";
import timezone from "dayjs/plugin/timezone.js";
import { KbvPluginService } from "@/services/kbvPluginService";
import Loading from "@/components/Loading";
import NoData from "@/components/NoData";
const TIME_ZONE_VN = "Asia/Ho_Chi_Minh";

dayjs.extend(utc);
dayjs.extend(timezone);

const formatStatusLabel = (match) => {
  const statusNum = Number(match?.status ?? 0);
  if (statusNum === 1 || statusNum === 2 || statusNum === 3) {
    return {
      timeText: `${Math.max(Number(match?.liveTime ?? 1), 1)}'`,
      isLive: true,
      liveBadge: "LIVE",
    };
  }
  if (statusNum === -1) {
    return { timeText: "HT", isLive: true, liveBadge: "HT" };
  }
  if (statusNum === 4) {
    return { timeText: "FT", isLive: false, liveBadge: "" };
  }
  if (match?.matchTime) {
    return {
      timeText: dayjs.unix(Number(match.matchTime)).tz(TIME_ZONE_VN).format("HH:mm"),
      isLive: false,
      liveBadge: "",
    };
  }
  return { timeText: "--:--", isLive: false, liveBadge: "" };
};

const normalizeScore = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  return Number.isNaN(num) ? "-" : String(num);
};

const MatchRow = ({ match, isLast }) => {
  const { timeText, isLive, liveBadge } = formatStatusLabel(match);
  const statusNum = Number(match?.status ?? 0);
  const isNotStarted = statusNum === 0;
  const homeScore = isNotStarted ? "-" : normalizeScore(match?.homeScore);
  const awayScore = isNotStarted ? "-" : normalizeScore(match?.awayScore);

  return (
    <div className="relative px-3 py-1.5">
      <div className="flex items-center gap-2 mb-1">
        <span className={`text-[12px] font-bold leading-5 ${isLive ? "text-red-500" : "text-[var(--theme-subtext)]"}`}>
          {timeText}
        </span>
        {liveBadge ? (
          <span className="inline-flex items-center justify-center min-w-[36px] h-5 px-1.5 rounded-md bg-red-500 text-white text-[11px] font-bold leading-none">
            {liveBadge}
          </span>
        ) : null}
      </div>
      <div className="grid grid-cols-[minmax(0,1fr)_22px] gap-2">
        <div className="min-w-0">
          <div className="flex items-start gap-2 min-w-0">
            {match?.homeTeam?.logo ? (
              <img
                src={match.homeTeam.logo}
                alt={match?.homeName || "home"}
                className="w-6 h-6 object-cover rounded-full shrink-0 mt-0.5"
                loading="lazy"
              />
            ) : null}
            <p
              className="m-0 line-clamp-2 break-words text-[12px] sm:text-[13px] font-semibold leading-5 text-[var(--theme-text)]"
              title={match?.homeTeam?.nameVi || match?.homeName || "-"}
            >
              {match?.homeTeam?.nameVi || match?.homeName || "-"}
            </p>
          </div>
          <div className="flex items-start gap-2 min-w-0 mt-1">
            {match?.awayTeam?.logo ? (
              <img
                src={match.awayTeam.logo}
                alt={match?.awayName || "away"}
                className="w-6 h-6 object-cover rounded-full shrink-0 mt-0.5"
                loading="lazy"
              />
            ) : null}
            <p
              className="m-0 line-clamp-2 break-words text-[12px] sm:text-[13px] font-semibold leading-5 text-[var(--theme-text)]"
              title={match?.awayTeam?.nameVi || match?.awayName || "-"}
            >
              {match?.awayTeam?.nameVi || match?.awayName || "-"}
            </p>
          </div>
        </div>
        <div className="flex flex-col items-end justify-center">
          <span className="text-[13px] sm:text-[14px] font-bold leading-5 text-red-500 tabular-nums">{homeScore}</span>
          <span className="text-[13px] sm:text-[14px] font-bold leading-5 text-red-500 tabular-nums mt-1">{awayScore}</span>
        </div>
      </div>
      {!isLast ? <span className="absolute left-3 right-3 bottom-0 h-px bg-[var(--theme-border)]" /> : null}
    </div>
  );
};

const KbvHotMatches = () => {
  const [loading, setLoading] = useState(true);
  const [matches, setMatches] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchHotPick() {
      setLoading(true);
      setError(null);
      try {
        const toDate = dayjs().tz(TIME_ZONE_VN).add(15, "day").format("YYYY-MM-DD");
        const res = await KbvPluginService.fetchBangKeoMatchesOld({
          filterInclude: `hotTeam:1,toDate:${toDate}`,
          include: "homeTeam,awayTeam,odd",
          status: "prepare",
          page: 1,
          limit: 10,
        });

        if (cancelled) return;
        const data = Array.isArray(res?.data?.data) ? res.data.data : [];
        const sorted = [...data].sort((a, b) => {
          const ta = Number(a?.matchTime) || 0;
          const tb = Number(b?.matchTime) || 0;
          return ta - tb;
        });
        setMatches(sorted);
      } catch (e) {
        if (cancelled) return;
        setError(e);
        setMatches([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchHotPick();
    return () => {
      cancelled = true;
    };
  }, []);

  const visibleMatches = matches.slice(0, 8);
  if (loading) return <Loading />;

  return (
    <div className="w-full max-w-full rounded-xl border border-[var(--theme-border)] bg-[var(--theme-foreground)] overflow-hidden shadow-sm">
      <div className="px-3 py-2 bg-gradient-to-r from-[#062866] to-[#001433] border-b border-[var(--theme-border)]">
        <div className="m-0 text-[16px] sm:text-[17px] font-extrabold tracking-tight text-white uppercase">
          <span className="mr-2" role="img" aria-label="fire">
            🔥
          </span>
          Trận đấu hot
        </div>
      </div>
      {error ? (
        <div className="py-2 px-3">
          <NoData message="Không tải được dữ liệu trận đấu hot" />
        </div>
      ) : null}
      {!error && visibleMatches.length === 0 ? (
        <div className="py-2 px-3">
          <NoData message="Chưa có trận đấu hot" />
        </div>
      ) : null}
      {!error && visibleMatches.length > 0 ? (
        <div className="w-full">{visibleMatches.map((match, index) => <MatchRow key={match.id} match={match} isLast={index === visibleMatches.length - 1} />)}</div>
      ) : null}
    </div>
  );
};

export default KbvHotMatches;